import React, { useState } from 'react';
import {
  FiX, FiZap, FiRefreshCw, FiAlertTriangle, FiCheckCircle,
  FiTrendingUp, FiShield, FiActivity, FiSun, FiDollarSign,
} from 'react-icons/fi';
import {
  optimizeArchitecture,
  type OptimizationItem, type OptimizationResult, type AiCacheHistoryEntry,
} from '../../api/ai';
import AiCacheActions from './AiCacheActions';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
  architectureId?: string;
  costs?: any;
}

const CATEGORY_CONFIG = {
  cost:           { label: 'Coût',          icon: <FiDollarSign size={13} />, color: 'text-green-600',  bg: 'bg-green-50 border-green-200'  },
  performance:    { label: 'Performance',   icon: <FiTrendingUp size={13} />, color: 'text-blue-600',   bg: 'bg-blue-50 border-blue-200'    },
  security:       { label: 'Sécurité',      icon: <FiShield size={13} />,     color: 'text-red-600',    bg: 'bg-red-50 border-red-200'      },
  reliability:    { label: 'Résilience',    icon: <FiActivity size={13} />,   color: 'text-purple-600', bg: 'bg-purple-50 border-purple-200' },
  sustainability: { label: 'Durabilité',    icon: <FiSun size={13} />,        color: 'text-teal-600',   bg: 'bg-teal-50 border-teal-200'    },
} as const;

const EFFORT_CONFIG  = { low: { label: 'Faible',  cls: 'bg-green-100 text-green-700' }, medium: { label: 'Moyen',  cls: 'bg-yellow-100 text-yellow-700' }, high: { label: 'Élevé', cls: 'bg-red-100 text-red-700' } };
const IMPACT_CONFIG  = { low: { label: 'Faible',  cls: 'bg-gray-100 text-gray-600'   }, medium: { label: 'Moyen',  cls: 'bg-blue-100 text-blue-700'    }, high: { label: 'Élevé', cls: 'bg-indigo-100 text-indigo-700' } };

function ScoreRing({ score }: { score: number }) {
  const r = 36; const circ = 2 * Math.PI * r;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : score >= 40 ? '#f97316' : '#ef4444';
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={88} height={88}>
        <circle cx={44} cy={44} r={r} fill="none" stroke="#e5e7eb" strokeWidth={8} />
        <circle cx={44} cy={44} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 44 44)" />
      </svg>
      <span className="absolute text-xl font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

function OptimizationCard({ opt, isQuickWin }: { opt: OptimizationItem; isQuickWin: boolean }) {
  const [open, setOpen] = useState(false);
  const cat = CATEGORY_CONFIG[opt.category] || CATEGORY_CONFIG.cost;
  return (
    <div className={`border rounded-xl overflow-hidden ${cat.bg}`}>
      <button className="w-full text-left p-4" onClick={() => setOpen(!open)}>
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 ${cat.color}`}>{cat.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-sm font-semibold text-gray-800">{opt.title}</span>
              {isQuickWin && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium border border-amber-300">⚡ Quick win</span>
              )}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${cat.color} border`} style={{ borderColor: 'currentColor', opacity: 0.7 }}>{cat.label}</span>
            </div>
            <p className="text-xs text-gray-600">{opt.description}</p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${EFFORT_CONFIG[opt.effort]?.cls}`}>
              {EFFORT_CONFIG[opt.effort]?.label}
            </span>
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${IMPACT_CONFIG[opt.impact]?.cls}`}>
              ↑ {IMPACT_CONFIG[opt.impact]?.label}
            </span>
          </div>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-black/5 pt-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-red-50 border border-red-100 rounded-lg p-2">
              <p className="text-xs font-medium text-red-600 mb-1">Situation actuelle</p>
              <p className="text-xs text-gray-700">{opt.currentState}</p>
            </div>
            <div className="bg-green-50 border border-green-100 rounded-lg p-2">
              <p className="text-xs font-medium text-green-600 mb-1">Situation cible</p>
              <p className="text-xs text-gray-700">{opt.targetState}</p>
            </div>
          </div>
          {opt.implementationSteps?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1">Étapes d'implémentation :</p>
              <ol className="space-y-1">
                {opt.implementationSteps.map((step, i) => (
                  <li key={i} className="text-xs text-gray-600 flex gap-2">
                    <span className="text-indigo-500 font-bold shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {opt.estimatedSavingPercent != null && (
            <p className="text-xs text-green-700 font-medium">💰 Économie estimée : ~{opt.estimatedSavingPercent}%</p>
          )}
        </div>
      )}
    </div>
  );
}

const ContinuousOptimizationModal: React.FC<Props> = ({
  isOpen, onClose, nodes, edges, provider, environment, architectureName, architectureId, costs,
}) => {
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [result, setResult]           = useState<OptimizationResult | null>(null);
  const [activeTab, setActiveTab]     = useState<'all' | 'cost' | 'performance' | 'security' | 'reliability'>('all');
  const [fromCache, setFromCache]     = useState(false);
  const [cacheUpdatedAt, setCacheUpdatedAt] = useState<string | undefined>();

  const providerName = { azure: 'Azure', aws: 'AWS', gcp: 'Google Cloud' }[provider] || provider;

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await optimizeArchitecture({ nodes, edges, provider, environment, architectureName, costs });
      setResult(data);
      setFromCache(false);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = () => {
    if (!result) return;
    const { report, metadata } = result;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Optimisation Continue — ${architectureName}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;font-size:14px;color:#111}
      h1{font-size:20px;font-weight:700;border-bottom:2px solid #6366f1;padding-bottom:8px}
      h2{font-size:16px;font-weight:600;margin:20px 0 8px;color:#312e81}
      .card{border:1px solid #e0e7ff;border-radius:8px;padding:12px;margin:8px 0;background:#f8faff}
      .badge{display:inline-block;padding:2px 8px;border-radius:12px;font-size:11px;font-weight:600;margin-right:4px}
      .cost{background:#dcfce7;color:#166534}.perf{background:#dbeafe;color:#1e40af}
      .sec{background:#fee2e2;color:#991b1b}.rel{background:#ede9fe;color:#5b21b6}
      @media print{body{padding:16px}}
    </style></head><body>`);
    w.document.write(`<h1>Rapport d'Optimisation Continue — ${architectureName || providerName}</h1>`);
    w.document.write(`<p><b>Score :</b> ${report.overallScore}/100 | <b>Ressources analysées :</b> ${result.resourcesAnalyzed} | <b>Généré :</b> ${new Date(metadata.generatedAt).toLocaleString('fr-FR')}</p>`);
    w.document.write(`<p>${report.summary}</p>`);
    if (report.estimatedTotalSaving?.description) {
      w.document.write(`<p><b>💰 Potentiel d'économie :</b> ${report.estimatedTotalSaving.description}</p>`);
    }
    if (report.topPriorities?.length) {
      w.document.write(`<h2>Priorités</h2><ol>${report.topPriorities.map(p => `<li>${p}</li>`).join('')}</ol>`);
    }
    w.document.write('<h2>Recommandations</h2>');
    report.optimizations.forEach(opt => {
      const catMap: any = { cost: 'cost', performance: 'perf', security: 'sec', reliability: 'rel' };
      w.document.write(`<div class="card">
        <p><b>${opt.priority}. ${opt.title}</b> <span class="badge ${catMap[opt.category] || ''}">${opt.category}</span></p>
        <p>${opt.description}</p>
        <p><i>Actuel :</i> ${opt.currentState} → <i>Cible :</i> ${opt.targetState}</p>
        <p>Effort : ${opt.effort} | Impact : ${opt.impact}${opt.estimatedSavingPercent != null ? ` | Économie : ~${opt.estimatedSavingPercent}%` : ''}</p>
      </div>`);
    });
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (!isOpen) return null;

  const report = result?.report;
  const filteredOpts = report?.optimizations.filter(o => activeTab === 'all' || o.category === activeTab) || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiZap className="text-indigo-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Optimisation Continue</h2>
            <span className="text-xs text-gray-400 font-normal">{providerName} · {environment}</span>
          </div>
          <div className="flex items-center gap-2">
            {result && (
              <button onClick={handleAnalyze} className="text-xs text-gray-500 hover:text-indigo-600 px-2 py-1 rounded hover:bg-gray-50 transition-colors flex items-center gap-1">
                <FiRefreshCw size={12} /> Relancer
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Initial state */}
          {!result && !loading && (
            <div className="max-w-lg mx-auto space-y-5">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 text-center">
                <FiZap className="text-indigo-500 mx-auto mb-2" size={32} />
                <h3 className="font-semibold text-gray-800 mb-1">Analyse d'optimisation continue</h3>
                <p className="text-sm text-gray-600">
                  L'IA analyse ton architecture <strong>{providerName}</strong> et identifie des opportunités concrètes d'amélioration — coût, performance, sécurité, résilience.
                </p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Architecture analysée :</p>
                <p className="text-sm font-medium text-gray-700">{architectureName || 'Architecture sans nom'}</p>
                <p className="text-xs text-gray-500 mt-1">{nodes.filter(n => n.data?.type).length} ressources · {providerName}</p>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <FiAlertTriangle size={14} />{error}
                </div>
              )}
              <button onClick={handleAnalyze}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2"
                style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                <FiZap size={15} /> Lancer l'analyse
              </button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <FiRefreshCw size={28} className="text-indigo-500 animate-spin" />
              <p className="text-sm text-gray-600">Analyse en cours…</p>
            </div>
          )}

          {/* Results */}
          {result && report && (
            <div className="space-y-5">

              {/* Summary */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex flex-col items-center gap-1">
                  <ScoreRing score={report.overallScore} />
                  <span className="text-xs text-gray-500">Score optim.</span>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm text-gray-700 mb-2">{report.summary}</p>
                  {report.estimatedTotalSaving?.description && (
                    <div className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-xs font-medium px-2.5 py-1 rounded-full border border-green-200">
                      <FiCheckCircle size={12} /> {report.estimatedTotalSaving.description}
                    </div>
                  )}
                </div>
                <div className="border-l border-indigo-100 pl-4 text-xs text-gray-500 space-y-1">
                  <p>Ressources : <span className="font-medium text-gray-700">{result.resourcesAnalyzed}</span></p>
                  <p>Recommandations : <span className="font-medium text-gray-700">{report.optimizations.length}</span></p>
                  <p>Quick wins : <span className="font-medium text-amber-600">{report.quickWins?.length || 0}</span></p>
                </div>
              </div>

              {/* Top priorities */}
              {report.topPriorities?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs font-semibold text-amber-700 mb-2 uppercase tracking-wide">Priorités immédiates</p>
                  <ol className="space-y-1">
                    {report.topPriorities.map((p, i) => (
                      <li key={i} className="text-sm text-gray-700 flex gap-2">
                        <span className="text-amber-500 font-bold shrink-0">{i + 1}.</span>{p}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Category tabs */}
              <div className="flex gap-1 flex-wrap">
                {(['all', 'cost', 'performance', 'security', 'reliability'] as const).map(tab => {
                  const count = tab === 'all' ? report.optimizations.length : report.optimizations.filter(o => o.category === tab).length;
                  if (tab !== 'all' && count === 0) return null;
                  const cfg = tab === 'all' ? null : CATEGORY_CONFIG[tab];
                  return (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${activeTab === tab ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'}`}>
                      {cfg ? <span className="flex items-center gap-1">{cfg.icon}{cfg.label} ({count})</span> : `Toutes (${count})`}
                    </button>
                  );
                })}
              </div>

              {/* Cards */}
              <div className="space-y-3">
                {filteredOpts
                  .sort((a, b) => a.priority - b.priority)
                  .map(opt => (
                    <OptimizationCard
                      key={opt.id}
                      opt={opt}
                      isQuickWin={report.quickWins?.includes(opt.id)}
                    />
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {result && (
          <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {result.metadata.tokensUsed} tokens · {result.metadata.durationMs}ms · {new Date(result.metadata.generatedAt).toLocaleTimeString('fr-FR')}
            </span>
            <div className="flex items-center gap-2">
              {architectureId && (
                <AiCacheActions
                  architectureId={architectureId}
                  cacheType="optimization"
                  content={result ? JSON.stringify(result.report) : ''}
                  fromCache={fromCache}
                  updatedAt={cacheUpdatedAt}
                  onRestore={(entry: AiCacheHistoryEntry) => {
                    try {
                      const report = typeof entry.content === 'string' ? JSON.parse(entry.content) : entry.content;
                      setResult({ report, resourcesAnalyzed: nodes.filter(n => n.data?.type).length, metadata: { model: '', tokensUsed: 0, durationMs: 0, generatedAt: entry.savedAt } });
                      setFromCache(true);
                      setCacheUpdatedAt(entry.savedAt);
                    } catch {}
                  }}
                  onExportPdf={handleExportPdf}
                />
              )}
              <button onClick={handleExportPdf}
                className="px-3 py-1.5 text-xs font-medium bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Export PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContinuousOptimizationModal;
